import { Button, ButtonGroup, cn, DateRangePicker } from '@heroui/react';
import { DateValue, parseZonedDateTime } from '@internationalized/date';
import { I18nProvider } from '@react-aria/i18n';
import { Calendar } from 'iconoir-react';
import { useCallback, useMemo, useState } from 'react';

import { TimeOption, TimeRangeWithTabsProps } from './types';
import { useTimeRangeWithTabs } from './useTimeRangeWithTabs';

import i18n from '~/common/helpers/i18n';

export function TimeRangeWithTabs({
  defaultValue,
  value,
  onChange,
  timeOptions,
  minDate,
  maxDate,
  testIdPrefix
}: TimeRangeWithTabsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const [pendingDateRange, setPendingDateRange] = useState<{
    start: DateValue;
    end: DateValue;
  } | null>(null);

  const {
    isCustomRange,
    dateRange,
    selectOption,
    selectCustomRange,
    isOptionSelected,
    getDefaultDateRange
  } = useTimeRangeWithTabs({
    defaultValue,
    value,
    onChange,
    timeOptions
  });

  const handleQuickOptionClick = useCallback(
    (option: TimeOption) => {
      selectOption(option.value);
      setPendingDateRange(null);
    },
    [selectOption]
  );

  const handleDateRangeChange = useCallback(
    (range: { start: DateValue; end: DateValue } | null) => {
      if (range) {
        const currentRange = pendingDateRange ?? dateRange;
        const startDateChanged =
          !currentRange ||
          range.start.year !== currentRange.start.year ||
          range.start.month !== currentRange.start.month ||
          range.start.day !== currentRange.start.day;
        const endDateChanged =
          !currentRange ||
          range.end.year !== currentRange.end.year ||
          range.end.month !== currentRange.end.month ||
          range.end.day !== currentRange.end.day;

        if (isOpen && (startDateChanged || endDateChanged)) {
          const normalizedStart = startDateChanged
            ? range.start.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            : range.start;
          const normalizedEnd = endDateChanged
            ? range.end.set({
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 999
              })
            : range.end;
          setPendingDateRange({ start: normalizedStart, end: normalizedEnd });
        } else {
          setPendingDateRange(range);
        }
      }
    },
    [isOpen, pendingDateRange, dateRange]
  );

  const handleBlur = useCallback(() => {
    if (pendingDateRange && !isOpen) {
      selectCustomRange(pendingDateRange);
    }
  }, [pendingDateRange, isOpen, selectCustomRange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && pendingDateRange && !isOpen) {
        selectCustomRange(pendingDateRange);
      }
    },
    [pendingDateRange, isOpen, selectCustomRange]
  );

  const handleApply = useCallback(() => {
    if (pendingDateRange) {
      selectCustomRange(pendingDateRange);
    } else if (dateRange) {
      selectCustomRange(dateRange);
    } else {
      const defaultRange = getDefaultDateRange();
      selectCustomRange(defaultRange);
    }
    setPendingDateRange(null);
    setIsOpen(false);
  }, [pendingDateRange, dateRange, selectCustomRange, getDefaultDateRange]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        if (pendingDateRange) {
          selectCustomRange(pendingDateRange);
        }
        setPendingDateRange(null);
      }
    },
    [pendingDateRange, selectCustomRange]
  );

  const minValue = useMemo(() => {
    if (!minDate) return undefined;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isoString = minDate.toISOString();
    const dateStr = isoString.split('T')[0];
    return parseZonedDateTime(`${dateStr}T00:00:00.000[${userTimezone}]`);
  }, [minDate]);

  const maxValue = useMemo(() => {
    if (!maxDate) return undefined;
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isoString = maxDate.toISOString();
    const dateStr = isoString.split('T')[0];
    return parseZonedDateTime(`${dateStr}T23:59:59.999[${userTimezone}]`);
  }, [maxDate]);

  const handleCustomRangeClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const getTestId = useCallback(
    (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix),
    [testIdPrefix]
  );

  return (
    <div
      className='flex items-center'
      data-testid={getTestId('time-range-container')}
    >
      <ButtonGroup>
        {timeOptions.map(option => {
          const isSelected = isOptionSelected(option.value);

          return (
            <>
              {option.value === 'specific' && (
                <I18nProvider locale={navigator.language}>
                  <DateRangePicker
                    aria-label={
                      i18n.t(
                        'scenes.monitor.overview.labels.absolute_time_range'
                      )!
                    }
                    CalendarBottomContent={
                      <div className='pb-4 pr-5 pt-2 flex justify-end gap-1.5'>
                        <Button
                          variant='light'
                          size='sm'
                          className='text-label-md px-1.5 min-w-0 rounded'
                          onPress={() => setPendingDateRange(null)}
                        >
                          {i18n.t('label.clear')}
                        </Button>
                        <Button
                          color='primary'
                          size='sm'
                          className='text-label-md text-on-secondary rounded'
                          onPress={handleApply}
                        >
                          {i18n.t('label.apply')}
                        </Button>
                      </div>
                    }
                    className={cn('w-0', isCustomRange && 'ml-[-1px] w-auto')}
                    classNames={{
                      base: '!p-0',
                      calendar: 'bg-white -mt-2',
                      timeInput: 'pb-0',
                      selectorIcon: 'icon-smallest',
                      calendarContent:
                        '[&_[data-today="true"]]:ring-1 [&_[data-today="true"]]:ring-gray-500',
                      inputWrapper: cn(
                        'p-2 py-1 min-h-[undefined] h-auto',
                        isCustomRange
                          ? 'bg-surface-container-low border-[#00000052] border-solid border rounded-s-none'
                          : 'bg-transparent'
                      ),
                      innerWrapper: cn(
                        'invisible w-0 h-5 text-label-lg',
                        isCustomRange && 'visible w-auto'
                      ),
                      segment: 'text-label-lg'
                    }}
                    selectorIcon={<Calendar className='h-4 w-4' />}
                    value={
                      pendingDateRange ?? dateRange ?? getDefaultDateRange()
                    }
                    onChange={handleDateRangeChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    isOpen={isOpen}
                    onOpenChange={handleOpenChange}
                    granularity='minute'
                    hideTimeZone
                    visibleMonths={2}
                    minValue={minValue}
                    maxValue={maxValue}
                    popoverProps={{
                      placement: 'bottom-start',
                      crossOffset: -8
                    }}
                    timeInputProps={
                      { 'data-testid': getTestId('time-range-input') } as never
                    }
                    data-testid={getTestId('time-range-custom')}
                  />
                </I18nProvider>
              )}
              <Button
                key={option.value}
                type='button'
                onPress={
                  option.value === 'specific'
                    ? handleCustomRangeClick
                    : () => handleQuickOptionClick(option)
                }
                variant='bordered'
                size='sm'
                className={cn(
                  'transition-colors border border-outline text-title-sm py-1 px-2 [&:not(:first-of-type)]:ml-[-1px] h-auto first-of-type:!rounded-s-md min-w-[52px]',
                  ((isSelected && !isOpen) ||
                    (option.value === 'specific' && isOpen)) &&
                    'bg-surface-container-low border-[#00000052] !text-label-lg',
                  option.value === 'specific' && isCustomRange && 'hidden',
                  option.value === 'specific' && '!rounded-e-md'
                )}
                data-testid={getTestId(`time-range-option-${option.value}`)}
              >
                {option.label}
              </Button>
            </>
          );
        })}
      </ButtonGroup>
    </div>
  );
}
